import React, { useState } from "react";
import { CaretRightIcon, WarningIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import type { businessNotesAll } from "common/types";

interface SpaceAlertsBannerProps {
  alerts: businessNotesAll[];
  isMapper: boolean;
  className?: string;
}

export default function SpaceAlertsBanner({
  alerts,
  isMapper,
  className,
}: SpaceAlertsBannerProps) {
  const i18n = useI18N();
  const [isExpanded, setIsExpanded] = useState(false);

  if (alerts.length === 0) return null;

  return (
    <section
      aria-labelledby="space-alerts-banner-heading"
      className={className}
    >
      <div className="bg-bg-error-tertiary-default rounded-lg border border-border-token-error-minimal hover:bg-bg-error-tertiary-hover">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-error-100 transition-colors rounded-lg focus-blue"
          aria-expanded={isExpanded}
          aria-controls="space-alerts-banner-list"
        >
          <div className="flex items-center gap-3">
            <WarningIcon
              size={24}
              weight="fill"
              aria-hidden="true"
              className="text-fg-error-subtle"
            />
            <h3
              id="space-alerts-banner-heading"
              className="label-m-semibold text-fg-neutral-strong"
            >
              {i18n.get("accessibilityAlerts")}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="label-s-medium text-fg-neutral-strong">
              {alerts.length}
            </span>
            <CaretRightIcon
              size={16}
              aria-hidden="true"
              weight="bold"
              className={`text-fg-neutral-strong transition-transform ${isExpanded ? "rotate-90" : ""}`}
            />
          </div>
        </button>

        {isExpanded && (
          <ul
            id="space-alerts-banner-list"
            className="px-3 pb-3 space-y-3"
          >
            {alerts.map((alert) => {
              const roles = alert.business_note_by_user_roles;
              const authorName = isMapper
                ? alert.business_note_by_user
                : roles?.isSociabilityMapper || roles?.isSociabilityTeam
                  ? "Sociability team"
                  : roles
                    ? alert.business_note_by_user
                    : "Sociability team";

              return (
                <li key={alert.business_note_text_id}>
                  <article className="bg-bg-brand-secondary-default rounded-lg p-4">
                    <div className="mb-2">
                      <p className="label-s-medium">
                        {alert.formatedScheduleDateFrom &&
                          alert.formatedScheduleDateTo && (
                            <>
                              <span className="text-blue-600">
                                {alert.formatedScheduleDateFrom} to{" "}
                                {alert.formatedScheduleDateTo}
                              </span>
                              <span> • </span>
                            </>
                          )}
                        <span className="text-fg-neutral-strong">
                          {`Added on ${alert.formatedCreatedAt} by ${authorName}`}
                        </span>
                      </p>
                    </div>
                    <h4 className="label-m-semibold text-fg-neutral-strong mb-2">
                      {alert.business_note_title}
                    </h4>
                    {alert.business_note_text && (
                      <p className="label-s-regular text-fg-neutral-strong">
                        {alert.business_note_text}
                      </p>
                    )}
                  </article>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
